// Complaint detail — single ticket timeline + sidebar actions. Mirrors the
// dispute detail surface (transitions, messages, assign) plus complaint-
// specific actions (priority change, SLA edit).

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import { DetailLayout, DetailSection, DefRow } from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  assignComplaint,
  fetchAdminComplaint,
  messageComplaint,
  setComplaintPriority,
  setComplaintSla,
  transitionComplaint,
  type AdminComplaintEvent,
  type ComplaintPriority,
  type ComplaintStatus,
} from '@/lib/admin-api';

const STATUS_LABELS: Record<ComplaintStatus, string> = {
  open: 'Open',
  in_progress: 'Take in progress',
  breached: 'Mark breached',
  closed: 'Close',
  cancelled: 'Cancel',
};

const PRIORITY_LIST: ComplaintPriority[] = ['urgent', 'high', 'med', 'low'];

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function eventLabel(e: AdminComplaintEvent): string {
  switch (e.kind) {
    case 'message':
      return 'Message';
    case 'status_change':
      return e.statusFrom ? `Status: ${e.statusFrom} → ${e.statusTo}` : `Status set to ${e.statusTo}`;
    case 'assignment':
      return 'Assignment';
    case 'priority_change':
      return `Priority: ${e.priorityFrom ?? '—'} → ${e.priorityTo ?? '—'}`;
    case 'sla_change':
      return 'SLA updated';
    default:
      return e.kind;
  }
}

export function ComplaintDetail() {
  const params = useParams({ strict: false }) as { id?: string };
  const id = params.id ?? '';
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'complaints', 'detail', id],
    queryFn: ({ signal }) => fetchAdminComplaint(id, signal),
    enabled: id.length > 0,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'complaints'] });
  };

  const transitionMut = useMutation({
    mutationFn: ({ to, reason }: { to: ComplaintStatus; reason?: string }) =>
      transitionComplaint(id, to, reason),
    onSuccess: invalidate,
  });
  const messageMut = useMutation({
    mutationFn: ({ body }: { body: string }) => messageComplaint(id, body),
    onSuccess: invalidate,
  });
  const assignMut = useMutation({
    mutationFn: ({ assigneeUserId }: { assigneeUserId: number | null }) =>
      assignComplaint(id, assigneeUserId),
    onSuccess: invalidate,
  });
  const priorityMut = useMutation({
    mutationFn: ({ priority }: { priority: ComplaintPriority }) =>
      setComplaintPriority(id, priority),
    onSuccess: invalidate,
  });
  const slaMut = useMutation({
    mutationFn: ({ slaDueAt, reason }: { slaDueAt: string | null; reason?: string }) =>
      setComplaintSla(id, slaDueAt, reason),
    onSuccess: invalidate,
  });

  const [draft, setDraft] = useState('');

  if (isLoading) {
    return (
      <DetailLayout title="Loading…" backTo="/complaints" backLabel="Complaints">
        <div className="text-[12.5px] text-[color:var(--bo-mute)]">Loading complaint…</div>
      </DetailLayout>
    );
  }
  if (error) {
    return (
      <DetailLayout
        title="Couldn't load complaint"
        sub={(error as Error).message}
        backTo="/complaints"
        backLabel="Complaints"
      >
        <div />
      </DetailLayout>
    );
  }
  if (!data) return null;

  const { complaint: c, events } = data;
  const mutationError =
    transitionMut.error ||
    messageMut.error ||
    assignMut.error ||
    priorityMut.error ||
    slaMut.error;
  const busy =
    transitionMut.isPending ||
    messageMut.isPending ||
    assignMut.isPending ||
    priorityMut.isPending ||
    slaMut.isPending;

  const handleTransition = (to: ComplaintStatus) => {
    const reason = window.prompt('Reason / note (optional):') ?? undefined;
    transitionMut.mutate({ to, reason });
  };

  const handleAssign = () => {
    const raw = window.prompt('Admin user ID to assign (blank to unassign):');
    if (raw === null) return;
    const trimmed = raw.trim();
    if (trimmed === '') {
      assignMut.mutate({ assigneeUserId: null });
      return;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n <= 0) {
      window.alert('Invalid admin user ID.');
      return;
    }
    assignMut.mutate({ assigneeUserId: n });
  };

  const handlePriority = () => {
    const next = window.prompt(`Priority? One of: ${PRIORITY_LIST.join(', ')}`, c.priority);
    if (!next) return;
    if (!PRIORITY_LIST.includes(next as ComplaintPriority)) {
      window.alert('Invalid priority.');
      return;
    }
    priorityMut.mutate({ priority: next as ComplaintPriority });
  };

  const handleSla = () => {
    const raw = window.prompt(
      'SLA due (ISO 8601, e.g. 2026-05-15T17:00:00Z). Leave blank to clear:',
      c.slaDueAt ?? '',
    );
    if (raw === null) return;
    const trimmed = raw.trim();
    if (trimmed === '') {
      slaMut.mutate({ slaDueAt: null });
      return;
    }
    const d = new Date(trimmed);
    if (!Number.isFinite(d.getTime())) {
      window.alert('Invalid date.');
      return;
    }
    slaMut.mutate({ slaDueAt: d.toISOString() });
  };

  const sendMessage = () => {
    const body = draft.trim();
    if (!body) return;
    messageMut.mutate(
      { body },
      {
        onSuccess: () => setDraft(''),
      },
    );
  };

  return (
    <DetailLayout
      eyebrow={`${c.kind} · ${c.channel}`}
      title={c.subject}
      sub={c.fromName ?? c.fromEmail ?? 'Anonymous reporter'}
      backTo="/complaints"
      backLabel="Complaints"
      actions={
        <>
          {c.allowedTransitions.map((to) => (
            <RowAction
              key={to}
              kind={to === 'closed' ? 'primary' : to === 'cancelled' ? 'danger' : undefined}
              busy={busy}
              onClick={() => handleTransition(to)}
            >
              {STATUS_LABELS[to]}
            </RowAction>
          ))}
          <RowAction busy={busy} onClick={handleAssign}>
            Assign
          </RowAction>
          <RowAction busy={busy} onClick={handlePriority}>
            Priority
          </RowAction>
          <RowAction busy={busy} onClick={handleSla}>
            SLA
          </RowAction>
          <StatusPill status={c.priority} />
          <StatusPill status={c.status} />
          {c.overdue && c.status !== 'breached' ? <StatusPill status="breached" /> : null}
        </>
      }
    >
      {mutationError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Action failed: {(mutationError as Error).message}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <DetailSection title="Reporter">
          <DefRow label="Name">{c.fromName ?? '—'}</DefRow>
          <DefRow label="Email">{c.fromEmail ?? '—'}</DefRow>
          {/* "Anonymous" used to show for every rider report, because
              fromUserId is an ADMIN id and a rider is a customer. Say which
              kind of account it is rather than implying we do not know who
              they are — we do. */}
          <DefRow label="Linked user">
            {c.fromUserId
              ? `Admin #${c.fromUserId}`
              : c.booking
                ? 'Customer (from the app)'
                : 'Anonymous'}
          </DefRow>
          <DefRow label="Channel">{c.channel}</DefRow>
        </DetailSection>

        <DetailSection title="Ticket">
          <DefRow label="Kind">{c.kind}</DefRow>
          <DefRow label="Priority">
            <StatusPill status={c.priority} />
          </DefRow>
          <DefRow label="Assigned">{c.assigneeEmail ?? 'Unassigned'}</DefRow>
          <DefRow label="SLA due">
            <span className={c.overdue ? 'font-semibold text-[#9f1239]' : ''}>
              {formatDateTime(c.slaDueAt)}
            </span>
          </DefRow>
        </DetailSection>

        <DetailSection title="Timestamps">
          <DefRow label="Opened">{formatDateTime(c.openedAt)}</DefRow>
          <DefRow label="Closed">{formatDateTime(c.closedAt)}</DefRow>
        </DetailSection>
      </div>

      {/* WHICH TRIP. A ticket saying "the driver never came" is unactionable
          without it — an operator cannot open the booking, see what was paid,
          or tell which crew to ask. Hidden entirely for complaints that carry
          no booking (billing questions, general support) rather than showing a
          card full of dashes. */}
      {c.booking ? (
        <DetailSection title="Booking">
          <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
            <DefRow label="Reference">
              <Link
                to="/bookings/$reference"
                params={{ reference: c.booking.reference ?? '' }}
                className="font-medium text-[color:var(--bo-ink)] underline"
              >
                {c.booking.reference ?? '—'}
              </Link>
            </DefRow>
            <DefRow label="Product">
              {c.booking.product === 'airport-transfers' ? 'Airport transfer' : 'Car rental'}
            </DefRow>
            <DefRow label="Status">{c.booking.status ?? '—'}</DefRow>
            <DefRow label="Trip date">{formatDateTime(c.booking.dateFrom)}</DefRow>
            <DefRow label="Pick-up">{c.booking.pickup ?? '—'}</DefRow>
            <DefRow label="Destination">{c.booking.destination ?? '—'}</DefRow>
            <DefRow label="Crew">{c.booking.crewName ?? '—'}</DefRow>
            <DefRow label="Driver">{c.booking.driverName ?? 'Not assigned'}</DefRow>
          </div>
        </DetailSection>
      ) : null}

      {c.body ? (
        <DetailSection title="Original message">
          <p className="whitespace-pre-wrap text-[13px] text-[color:var(--bo-ink)]">{c.body}</p>
        </DetailSection>
      ) : null}

      <DetailSection title="Timeline">
        {events.length === 0 ? (
          <div className="text-[12.5px] text-[color:var(--bo-mute)]">No events yet.</div>
        ) : (
          <ol className="relative ms-3 space-y-4 border-s border-[color:var(--bo-line)] ps-5">
            {events.map((e) => (
              <li key={e.id}>
                <span className="absolute -ms-[5.5px] mt-1.5 inline-block h-2 w-2 rounded-full bg-[color:var(--bo-accent)]" />
                <div className="flex items-baseline justify-between gap-3">
                  <div className="text-[12.5px] font-semibold">{eventLabel(e)}</div>
                  <div className="text-[11px] text-[color:var(--bo-mute)] tabular-nums">
                    {formatDateTime(e.createdAt)}
                  </div>
                </div>
                <div className="text-[11.5px] text-[color:var(--bo-mute)]">
                  {e.actorEmail ?? (e.actorRole === 'system' ? 'system' : e.actorRole)}
                </div>
                {e.body ? (
                  <p className="mt-1 whitespace-pre-wrap text-[12.5px] text-[color:var(--bo-ink)]">
                    {e.body}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        )}

        <div className="mt-4 rounded-lg border border-[color:var(--bo-line)] bg-white p-3">
          <label
            htmlFor="complaint-message"
            className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-[color:var(--bo-mute)]"
          >
            Add message
          </label>
          <textarea
            id="complaint-message"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Note for the complaint timeline (visible only to admins for now)…"
            rows={3}
            className="mt-2 w-full rounded-md border border-[color:var(--bo-line)] bg-white px-2 py-1.5 text-[13px] focus:border-[color:var(--bo-accent)] focus:outline-none"
          />
          <div className="mt-2 flex justify-end">
            <RowAction kind="primary" busy={messageMut.isPending} onClick={sendMessage}>
              Post message
            </RowAction>
          </div>
        </div>
      </DetailSection>
    </DetailLayout>
  );
}
